// ---------------------------------------------------------------------------
// searchMemorials — scalable memorial search.
//
// Instead of downloading every memorial and filtering in the browser, this
// runs two indexed Firestore queries against fields maintained by the
// searchIndexer Cloud Function:
//
//   nameTokens  — word prefixes ("smi" finds Smith, "pet" finds Peter)
//   nameLower   — "first last" for full-name prefix matches ("peter fa")
//
// Reads at most ~2× max documents no matter how many memorials exist.
// ---------------------------------------------------------------------------

import {
  collection, query, where, orderBy, startAt, endAt, limit, getDocs,
} from 'firebase/firestore';
import { db } from './firebase';

// Safety net for the window before the search index is first built (and for
// any memorial the indexer hasn't touched yet): scan a bounded number of
// memorials directly. Cheap today; at scale the indexed path answers first.
const FALLBACK_SCAN_LIMIT = 500;

async function fallbackScan(q, terms, max) {
  const snap = await getDocs(query(collection(db, 'memorials'), limit(FALLBACK_SCAN_LIMIT)));
  const out = [];
  snap.forEach((d) => {
    const m = d.data();
    const hay = [
      m.firstName, m.lastName, m.memorialTitle,
      m.galaxyLocation && m.galaxyLocation.starName,
    ].filter(Boolean).join(' ').toLowerCase();
    if (terms.every((t) => hay.includes(t))) out.push({ id: d.id, ...m });
  });
  return out.slice(0, max);
}

export async function searchMemorials(rawTerm, max = 40) {
  const q = (rawTerm || '').trim().toLowerCase();
  if (q.length < 2) return [];
  const terms = q.split(/\s+/).filter(Boolean);

  const queries = [
    // any word prefix (first name, last name, star name)
    getDocs(query(
      collection(db, 'memorials'),
      where('nameTokens', 'array-contains', terms[0].slice(0, 15)),
      limit(max)
    )),
    // full "first last" prefix
    getDocs(query(
      collection(db, 'memorials'),
      orderBy('nameLower'),
      startAt(q),
      endAt(q + ''),
      limit(max)
    )),
  ];

  const snaps = await Promise.allSettled(queries);
  const byId = new Map();
  for (const s of snaps) {
    if (s.status !== 'fulfilled') continue;
    s.value.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
  }

  let results = [...byId.values()];

  // Multi-word searches: every term must match a word prefix or the full name
  if (terms.length > 1) {
    results = results.filter((m) => {
      const tokens = new Set(m.nameTokens || []);
      const nameLower = m.nameLower || '';
      return terms.every((t) => tokens.has(t.slice(0, 15)) || nameLower.startsWith(q));
    });
  }

  if (results.length === 0) {
    try {
      return await fallbackScan(q, terms, max);
    } catch (e) {
      console.warn('search fallback scan failed:', e);
    }
  }

  return results.slice(0, max);
}
